import { Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface AIProcessingBannerProps {
  /** The message to display (already translated) */
  message: string;
  /** Optional className override */
  className?: string;
}

/**
 * Shared banner shown while AI is processing (document parsing, domain enrichment, etc.).
 *
 * Three stacked signals, loudest last: an `info`-tinted surface, a flashing
 * sparkles icon + shimmer sweep, and an indeterminate progress rail on the
 * bottom edge. The rail is the one that carries the state — the shimmer and the
 * flash only register if you catch them mid-cycle, whereas a travelling segment
 * reads as "working, duration unknown" at any glance.
 *
 * Keyed to `info`, not `primary`: `--primary` is a neutral near-black here, so a
 * primary-tinted waiting indicator is grey-on-grey and disappears.
 *
 * Usage:
 *   {isParsing && <AIProcessingBanner message={t('common:aiProcessing.analyzingDocument')} />}
 */
export function AIProcessingBanner({ message, className }: AIProcessingBannerProps) {
  return (
    <div
      role="status"
      className={cn(
        'ai-processing-banner relative overflow-hidden flex items-center gap-3 rounded-lg px-4 py-3.5',
        'bg-info/10 border border-info/40',
        className,
      )}
    >
      {/* Shimmer sweep overlay */}
      <div className="ai-processing-shimmer pointer-events-none absolute inset-0 rounded-lg" />

      {/* Animated sparkles icon */}
      <Sparkles className="ai-processing-sparkle relative h-5 w-5 shrink-0 text-info" />

      {/* Message */}
      <span className="relative text-sm font-medium text-foreground">
        {message}
      </span>

      {/* Indeterminate progress rail */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] overflow-hidden bg-info/20">
        <div className="ai-processing-rail-segment h-full w-2/5 rounded-full bg-info" />
      </div>
    </div>
  );
}
